import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { last } from 'lodash';

import { FlightEndpoint } from '@apps/main/modules/api/flight';
import {
  isHotelIngredient,
  isStayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';
import type {
  ContentApiFerryRouteStaticData,
  ContentApiFerrySuggestionRequestData,
} from '@apps/main/modules/api/content/contentApiResponses';
import { FerryEndpoint } from '@apps/main/modules/api/ferry';

import { SearchTermType } from '@libs/trip-planner-interfaces';
import type {
  AirportStaticInfo,
  Coordinate,
} from '@libs/trip-planner-interfaces';
import type { Location } from '@libs/maps';

const flightEndpoint = new FlightEndpoint();
const ferryEndpoint = new FerryEndpoint();

export function getDepartureSuggestedAirport(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  startingLocation?: Location
): Observable<AirportStaticInfo | undefined> {
  const departureSearchTerm = getDepartureSearchTerm(
    recipe,
    settingTransportIngredient,
    startingLocation
  );

  if (departureSearchTerm) {
    return flightEndpoint.getSuggestedAirport(departureSearchTerm);
  }

  return of(undefined);
}

export function getDepartureSearchTerm(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  startingLocation?: Location
): Coordinate | string | undefined {
  const index = recipe.findIndex(
    (ingredient) =>
      ingredient.ingredientId === settingTransportIngredient.ingredientId
  );

  if (index === -1) {
    const isEndLocationIngredient =
      isHotelIngredient(settingTransportIngredient) &&
      settingTransportIngredient.isDestinationPlaceholder;
    if (isEndLocationIngredient) {
      // NOTE: Setting transport ingredient on end location. Departure from last ingredient.
      const lastIngredient = last(recipe);
      if (!!lastIngredient && isHotelIngredient(lastIngredient)) {
        return (
          lastIngredient.positionFromSearchText ?? lastIngredient.displayText
        );
      }
    }

    return;
  }

  if (index === 0) {
    // NOTE: Setting transport ingredient on first ingredient. Departure from starting location.
    return startingLocation?.position;
  }

  // NOTE: Setting transport ingredient on ingredient in middle. Departure from previous ingredient.
  const previousIngredientIndex = index - 1;
  const previousIngredient = recipe.at(previousIngredientIndex);

  if (!!previousIngredient && isHotelIngredient(previousIngredient)) {
    return (
      previousIngredient.positionFromSearchText ??
      previousIngredient.displayText
    );
  }

  return;
}

export function getArrivalSuggestedAirport(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  endLocation?: Location
): Observable<AirportStaticInfo | undefined> {
  const arrivalSearchTerm = getArrivalSearchTerm(
    recipe,
    settingTransportIngredient,
    endLocation
  );

  if (arrivalSearchTerm) {
    return flightEndpoint.getSuggestedAirport(arrivalSearchTerm);
  }

  return of(undefined);
}

export function getArrivalSearchTerm(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  endLocation?: Location
): Coordinate | string | undefined {
  const index = recipe.findIndex(
    (ingredient) =>
      ingredient.ingredientId === settingTransportIngredient.ingredientId
  );

  if (index === -1) {
    const isEndLocationIngredient =
      isHotelIngredient(settingTransportIngredient) &&
      settingTransportIngredient.isDestinationPlaceholder;

    if (isEndLocationIngredient) {
      return endLocation?.position;
    }

    return;
  }

  if (isHotelIngredient(settingTransportIngredient)) {
    return (
      settingTransportIngredient.positionFromSearchText ??
      settingTransportIngredient.displayText
    );
  }

  return;
}

export function getFerryRoutesSuggestions(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  startLocation: Location | undefined,
  endLocation: Location | undefined
): Observable<ContentApiFerryRouteStaticData[]> {
  const from = getDepartureSeaportTerm(
    recipe,
    settingTransportIngredient,
    startLocation
  );
  const to = getArrivalSeaportTerm(
    recipe,
    settingTransportIngredient,
    endLocation
  );

  if (from && to) {
    return ferryEndpoint.getFerryRoutesSuggestions(from, to);
  }

  return of([]);
}

function getDepartureSeaportTerm(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  startingLocation?: Location
): ContentApiFerrySuggestionRequestData | undefined {
  const index = recipe.findIndex(
    (ingredient) =>
      ingredient.ingredientId === settingTransportIngredient.ingredientId
  );

  if (index === -1) {
    const isEndLocationIngredient =
      isHotelIngredient(settingTransportIngredient) &&
      settingTransportIngredient.isDestinationPlaceholder;
    if (isEndLocationIngredient) {
      // NOTE: Departure from last ingredient.
      const lastIngredient = last(recipe);
      if (
        !!lastIngredient &&
        isStayIngredient(lastIngredient) &&
        lastIngredient.searchTerm.type === SearchTermType.FreeText
      ) {
        return {
          location: lastIngredient.searchTerm.freeText,
        };
      }
    }

    return;
  }

  if (index === 0 && startingLocation?.position) {
    // NOTE: Departure from starting location.
    return {
      lat: startingLocation?.position.latitude,
      long: startingLocation?.position.longitude,
    };
  }

  const previousIngredientIndex = index - 1;
  const previousIngredient = recipe.at(previousIngredientIndex);
  if (
    !!previousIngredient &&
    isStayIngredient(previousIngredient) &&
    previousIngredient.positionFromSearchText
  ) {
    // NOTE: Departure from previous ingredient.
    return {
      lat: previousIngredient.positionFromSearchText.latitude,
      long: previousIngredient.positionFromSearchText.longitude,
    };
  }
}

function getArrivalSeaportTerm(
  recipe: RecipeMainIngredient[],
  settingTransportIngredient: RecipeMainIngredient,
  endLocation: Location | undefined
): ContentApiFerrySuggestionRequestData | undefined {
  const index = recipe.findIndex(
    (ingredient) =>
      ingredient.ingredientId === settingTransportIngredient.ingredientId
  );
  const nextIngredientIndex = index + 1;
  const nextIngredient = recipe.at(nextIngredientIndex);

  if (
    nextIngredient &&
    isStayIngredient(nextIngredient) &&
    nextIngredient.positionFromSearchText
  ) {
    return {
      lat: nextIngredient.positionFromSearchText.latitude,
      long: nextIngredient.positionFromSearchText.longitude,
    };
  }

  if (endLocation) {
    return {
      lat: endLocation.position.latitude,
      long: endLocation.position.longitude,
    };
  }
}
